import {
  Form,
  Input,
  Upload,
  Button,
  Radio,
  Switch,
  Select,
  TimePicker,
  DatePicker,
  Box,
  Tab,
  Dialog,
  Icon,
  Loading,
} from '@alifd/next';
import moment from 'moment';
import { useContext, useState } from 'react';
import MDEditor from '@uiw/react-md-editor';
import { BefGlobalContext, BefMessageServiceContext } from '@beflam/bef-core';
import {
  chatHistoryRequest,
  downloadChatExport,
} from '../../renderer/utils/chatHistoryApi';

const FormItem = Form.Item;
const RadioGroup = Radio.Group;
const TabPane = Tab.TabPane;

type HistoryItem = { Id: string; Title: string; Star: any; Date?: string };

const formatChatDate = (dateStr: string) => {
  const match = dateStr.match(
    /^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2}):(\d{2})/,
  );
  if (match) {
    return `${match[1]}年${match[2]}月${match[3]}日 ${match[4]}:${match[5]}:${match[6]}`;
  }
  return dateStr;
};

interface AssistantFormProps {
  editAssitant: any;
  isEditAssitant: boolean;
  handleSubmit: (values: any, errors: any) => void;
  onCancel: () => void;
  onDelete?: () => void;
  onHistorySelect?: (item: HistoryItem) => void;
  sGlobal: any;
  onDirtyChange?: (isDirty: boolean) => void;
}

export function AssistantForm(props: AssistantFormProps) {
  const {
    editAssitant,
    isEditAssitant,
    handleSubmit,
    onCancel,
    onDelete,
    onHistorySelect,
    sGlobal,
    onDirtyChange,
  } = props;

  const markDirty = () => onDirtyChange?.(true);

  const messageService = useContext(BefMessageServiceContext);

  const [scheduledTask, setScheduledTask] = useState<any>(
    editAssitant.ScheduledTask || false,
  );
  const [runType, setRunType] = useState<any>(editAssitant.RunType || 'daily');
  const [chatMode, setChatMode] = useState<any>(
    editAssitant.ChatMode || 'Execute',
  );
  const [promptValue, setPromptValue] = useState<string>(
    editAssitant.Prompt || '',
  );
  const [userInputPromptValue, setUserInputPromptValue] = useState<string>(
    editAssitant.UserInputPrompt || '',
  );
  const [historyItems, setHistoryItems] = useState<HistoryItem[]>([]);
  const [historyLoading, setHistoryLoading] = useState(false);

  const onScheduledTask = (isScheduledTask: any) => {
    setScheduledTask(isScheduledTask);
    markDirty();
  };

  const loadHistory = async () => {
    if (historyLoading) return;
    setHistoryLoading(true);
    try {
      const data = await chatHistoryRequest(sGlobal, {
        action: 'list',
        app_id: sGlobal.currentAppId,
        session_id: sGlobal.session.id,
        type_id: editAssitant.Name,
      });
      if (data && data.code === 0 && data.data) {
        setHistoryItems(data.data);
      } else if (data?.message) {
        messageService.showMessage(
          messageService.handleErr(data.message, '历史记录'),
        );
      }
    } catch (error) {
      messageService.showMessage(messageService.handleErr(error, '历史记录'));
    } finally {
      setHistoryLoading(false);
    }
  };

  const handleTabChange = (key: string) => {
    if (key === 'history') {
      loadHistory();
    }
  };

  const handleDownload = async (
    e: React.MouseEvent,
    conversationId: string,
  ) => {
    e.stopPropagation();
    try {
      const response = await downloadChatExport(sGlobal, conversationId);
      if (response.status !== 200) {
        let errMsg = `下载失败，HTTP状态：${response.status}`;
        try {
          const text = await response.text();
          if (text && text.length > 0) errMsg += `\n${text}`;
        } catch (_) {}
        messageService.showMessage(
          messageService.handleErr(errMsg, '下载聊天记录'),
        );
        return;
      }
      const blob = await response.blob();
      const downloadUrl = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = downloadUrl;
      a.download = `${conversationId}.txt`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(downloadUrl);
    } catch (error) {
      messageService.showMessage(
        messageService.handleErr(String(error), '下载聊天记录'),
      );
    }
  };

  return (
    <Form
      style={{ width: '60vw', minHeight: '70vh' }}
      size="large"
      labelCol={{ span: 3 }}
      wrapperCol={{ span: 20 }}
      labelAlign="left"
    >
      <Tab
        defaultActiveKey="main"
        lazyLoad={false}
        onChange={handleTabChange}
        style={{ minHeight: '65vh', marginTop: '-30px' }}
      >
        <TabPane
          key="main"
          title={<span style={{ fontSize: '16px' }}>主要</span>}
        >
          <FormItem
            label="名称"
            name="Name"
            required
            style={{ marginTop: 10 }}
            labelCol={{ span: 3 }}
            wrapperCol={{ span: 20 }}
          >
            <Input
              placeholder="请输入助理名称"
              defaultValue={editAssitant.Name || ''}
              disabled={isEditAssitant ? true : false}
              onChange={markDirty}
            />
          </FormItem>
          <FormItem
            label="提示词"
            name="Prompt"
            required
            labelCol={{ span: 3 }}
            wrapperCol={{ span: 20 }}
          >
            <MDEditor
              height={chatMode === 'Combine' ? '30vh' : '40vh'}
              value={promptValue}
              onChange={(value: any) => {
                setPromptValue(value || '');
                markDirty();
              }}
              data-color-mode="light"
              preview="edit"
              textareaProps={{ placeholder: '请输入提示词' }}
            />
          </FormItem>
          <FormItem
            label="对话模式"
            name="ChatMode"
            labelCol={{ span: 3 }}
            wrapperCol={{ span: 20 }}
          >
            <Select
              style={{ width: '100%' }}
              dataSource={[
                { label: '自动运行提示词', value: 'Execute' },
                { label: '在对话框中显示提示词', value: 'Input' },
                { label: '等待输入后运行提示词', value: 'Combine' },
              ]}
              defaultValue={editAssitant.ChatMode || 'Execute'}
              onChange={(val) => {
                setChatMode(val);
                markDirty();
              }}
            />
          </FormItem>
          {chatMode === 'Combine' && (
            <FormItem
              label="用户提示"
              name="UserInputPrompt"
              asterisk
              labelCol={{ span: 3 }}
              wrapperCol={{ span: 20 }}
            >
              <MDEditor
                height="20vh"
                value={userInputPromptValue}
                onChange={(value: any) => {
                  setUserInputPromptValue(value || '');
                  markDirty();
                }}
                data-color-mode="light"
                preview="edit"
                textareaProps={{ placeholder: '请输入用户输入提示' }}
              />
            </FormItem>
          )}
        </TabPane>
        <TabPane
          key="other"
          title={<span style={{ fontSize: '16px' }}>其他</span>}
        >
          <FormItem
            style={{ marginTop: 10 }}
            label="头像"
            name="Avatar"
            data-extra="支持 jpg, png 格式"
            labelCol={{ span: 3 }}
            wrapperCol={{ span: 20 }}
          >
            <Upload
              defaultValue={
                editAssitant.Avatar
                  ? [
                      {
                        name: editAssitant.Avatar.split('/').pop(),
                        state: 'done',
                        downloadURL:
                          sGlobal.config.host + '/' + editAssitant.Avatar,
                        imgURL: sGlobal.config.host + '/' + editAssitant.Avatar,
                      },
                    ]
                  : undefined
              }
              action={sGlobal.config.host + '/upload'}
              listType="text"
              limit={1}
              data={{
                path: `temp/import/${sGlobal.currentAppId}/${sGlobal.session.userId}/assistant`,
                sessionId: sGlobal.session.id,
              }}
              accept="image/png, image/jpg, image/jpeg, image/gif, image/bmp"
              formatter={(response: any) => ({
                success: response.success,
                message: response.success ? '上传成功' : response.error,
                fileId: response.fileId,
              })}
            >
              <Button type="primary" style={{ margin: '0 0 10px' }}>
                Upload
              </Button>
            </Upload>
          </FormItem>
          <FormItem
            label="意图模式"
            name="PageMode"
            labelCol={{ span: 3 }}
            wrapperCol={{ span: 20 }}
          >
            <RadioGroup
              dataSource={[
                { label: 'Auto', value: 'Auto' },
                { label: 'API', value: 'Biz' },
                { label: 'UI', value: 'Page' },
              ]}
              shape="button"
              size="medium"
              defaultValue={editAssitant.PageMode || 'Auto'}
              onChange={markDirty}
            />
          </FormItem>
          <FormItem
            label="深度思考"
            name="DeepThink"
            labelCol={{ span: 3 }}
            wrapperCol={{ span: 20 }}
          >
            <Switch
              defaultChecked={editAssitant.DeepThink || false}
              onChange={markDirty}
            />
          </FormItem>
          {/* <FormItem
            label="自动对话"
            name="AutoChat"
            labelCol={{ span: 3 }}
            wrapperCol={{ span: 20 }}
          >
            <Switch
              defaultChecked={editAssitant.AutoChat !== false}
              onChange={markDirty}
            />
          </FormItem> */}
          <FormItem
            label="定时任务"
            name="ScheduledTask"
            labelCol={{ span: 3 }}
            wrapperCol={{ span: 20 }}
          >
            <Switch
              defaultChecked={scheduledTask || false}
              onChange={onScheduledTask}
            />
          </FormItem>
          {scheduledTask ? (
            <FormItem
              label="运行时间"
              required
              labelCol={{ span: 3 }}
              wrapperCol={{ span: 20 }}
            >
              <Box direction="row" align="center" spacing={10}>
                <FormItem
                  name="RunType"
                  style={{ marginBottom: 0 }}
                  labelCol={{ span: 3 }}
                  wrapperCol={{ span: 20 }}
                >
                  <Select
                    dataSource={[
                      { label: '每天', value: 'daily' },
                      { label: '每周', value: 'weekly' },
                      { label: '每小时', value: 'hour' },
                      { label: '每分钟', value: 'minute' },
                    ]}
                    defaultValue={editAssitant.RunType || 'daily'}
                    onChange={(val) => {
                      setRunType(val);
                      markDirty();
                    }}
                  />
                </FormItem>

                {runType === 'weekly' && (
                  <FormItem
                    name="RunWeeks"
                    style={{ marginBottom: 0 }}
                    required
                    labelCol={{ span: 3 }}
                    wrapperCol={{ span: 20 }}
                  >
                    <Select
                      mode="multiple"
                      placeholder="周几"
                      dataSource={[
                        { label: '周一', value: 1 },
                        { label: '周二', value: 2 },
                        { label: '周三', value: 3 },
                        { label: '周四', value: 4 },
                        { label: '周五', value: 5 },
                        { label: '周六', value: 6 },
                        { label: '周日', value: 7 },
                      ]}
                      defaultValue={editAssitant.RunWeeks}
                      onChange={markDirty}
                    />
                  </FormItem>
                )}

                {(runType === 'daily' ||
                  runType === 'weekly' ||
                  runType === 'hour' ||
                  runType === 'minute') && (
                  <FormItem
                    name="RunTime"
                    style={{ marginBottom: 0 }}
                    required
                    labelCol={{ span: 3 }}
                    wrapperCol={{ span: 20 }}
                  >
                    <TimePicker
                      format={runType === 'minute' ? 'mm' : 'HH:mm'}
                      placeholder="选择时间"
                      defaultValue={editAssitant.RunTime || '00:00'}
                      onChange={markDirty}
                    />
                  </FormItem>
                )}
              </Box>
            </FormItem>
          ) : null}
          {scheduledTask ? (
            <FormItem
              label="到期日期"
              name="ExpireTime"
              labelCol={{ span: 3 }}
              wrapperCol={{ span: 20 }}
            >
              <DatePicker
                placeholder="请选择到期日期"
                format="YYYY-M-D"
                disabledDate={(date) => {
                  return date.isBefore(moment(), 'day');
                }}
                defaultValue={editAssitant.ExpireTime}
                onChange={markDirty}
              />
            </FormItem>
          ) : null}
          {scheduledTask ? (
            <FormItem
              label="通知方式"
              name="NotifyMethods"
              required
              labelCol={{ span: 3 }}
              wrapperCol={{ span: 20 }}
            >
              <Select
                mode="multiple"
                placeholder="请选择通知方式"
                defaultValue={editAssitant.NotifyMethods || ['Email']}
                onChange={markDirty}
                dataSource={[
                  { label: '邮件', value: 'Email' },
                  { label: '飞书', value: 'Lark' },
                  { label: '企业微信', value: 'Wework' },
                ]}
              />
            </FormItem>
          ) : null}
        </TabPane>
        {isEditAssitant && (
          <TabPane
            key="history"
            title={<span style={{ fontSize: '16px' }}>历史</span>}
          >
            <Loading
              visible={historyLoading}
              inline={false}
              style={{ minHeight: 200 }}
            >
              {!historyLoading && historyItems.length === 0 ? (
                <div
                  style={{
                    padding: '40px 0',
                    textAlign: 'center',
                    color: '#999',
                  }}
                >
                  暂无聊天历史
                </div>
              ) : (
                <div
                  style={{
                    maxHeight: '60vh',
                    overflowY: 'auto',
                    marginTop: 10,
                  }}
                >
                  {historyItems.map((item) => (
                    <div
                      key={item.Id}
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        padding: '10px 12px',
                        borderBottom: '1px solid #f0f0f0',
                        cursor: 'pointer',
                        transition: 'background-color 0.2s',
                      }}
                      onMouseEnter={(e) =>
                        (e.currentTarget.style.backgroundColor = '#f5f5f5')
                      }
                      onMouseLeave={(e) =>
                        (e.currentTarget.style.backgroundColor = 'transparent')
                      }
                      onClick={() => onHistorySelect?.(item)}
                    >
                      <span
                        style={{
                          flex: 1,
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap',
                        }}
                      >
                        {item.Date ? (
                          <>
                            <span style={{ color: '#888' }}>
                              {formatChatDate(item.Date)}
                            </span>
                            <span style={{ color: '#000000', margin: '0 6px' }}>
                              |
                            </span>
                            {item.Title}
                          </>
                        ) : (
                          item.Title
                        )}
                      </span>
                      {item.Star ? (
                        <Icon
                          type="favorites-filling"
                          size="xs"
                          style={{
                            color: '#faad14',
                            marginRight: 8,
                            flexShrink: 0,
                          }}
                        />
                      ) : null}
                      <button
                        className="assistant-close-button"
                        title="下载聊天记录"
                        style={{ flexShrink: 0 }}
                        onClick={(e) => handleDownload(e, item.Id)}
                      >
                        <Icon type="download" size="xs" />
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </Loading>
          </TabPane>
        )}
      </Tab>
      <FormItem label=" " wrapperCol={{ offset: 12 }}>
        <Form.Submit
          type="primary"
          validate
          onClick={handleSubmit}
          style={{ marginRight: 18 }}
        >
          {isEditAssitant ? '确认修改' : '确认添加'}
        </Form.Submit>
        <Button onClick={onCancel} style={{ marginRight: 18 }}>
          取消
        </Button>
        {isEditAssitant && onDelete && (
          <Button
            type="normal"
            warning
            onClick={() => {
              Dialog.confirm({
                title: '确认删除',
                content: '确定要删除此助理吗？此操作不可撤销。',
                closeMode: ['close', 'esc', 'mask'],
                onOk: () => {
                  onDelete();
                  return new Promise((resolve) => setTimeout(resolve, 500));
                },
              });
            }}
          >
            删除
          </Button>
        )}
      </FormItem>
    </Form>
  );
}
